import { useState } from "react";
import { useSelector } from "react-redux";
import { Select } from "antd";
import dayjs from "dayjs";
import DateRangeFilter from "../../components/dateRangeFilter";
import StatCard from "../../components/dashboards/StatCard";
import CommonTable from "../../components/commonTable";
import { DollarOutlined, WalletOutlined, BankOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useGetCashReportsQuery, useGetCashSummaryReportQuery } from "../../services/cash";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface DateRangeValue { start: Date; end: Date; }

type SummaryFilter = 'today' | 'monthly' | 'last_3_month' | 'last_6_month' | 'last_12_month';

const SUMMARY_FILTER_OPTIONS: { value: SummaryFilter; label: string }[] = [
  { value: 'today',         label: 'Today' },
  { value: 'monthly',       label: 'Monthly' },
  { value: 'last_3_month',  label: 'Last 3 Months' },
  { value: 'last_6_month',  label: 'Last 6 Months' },
  { value: 'last_12_month', label: 'Last 12 Months' },
];

interface BranchReportRow {
  key:             string;
  branchName?:     string;
  // openingBalance?: number;
  totalCollected?: number;
  totalDeposited?: number;
  closingBalance?: number;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const defaultDateRange = (): DateRangeValue => ({
  start: dayjs().subtract(14, 'day').startOf('day').toDate(),
  end:   dayjs().endOf('day').toDate(),
});

const inr = (v?: number) => `₹${(v ?? 0).toLocaleString('en-IN')}`;

const renderAmount = (v?: number) =>
  v === undefined || v === null ? '-' : <span>{inr(v)}</span>;

const columns = [
  { title: 'Branch',          dataIndex: 'branchName',     key: 'branchName',     render: (v?: string) => v || '-' },
  // { title: 'Opening Balance', dataIndex: 'openingBalance', key: 'openingBalance', render: renderAmount },
  { title: 'Total Collected', dataIndex: 'totalCollected', key: 'totalCollected', render: (v?: number) => <span style={{ color: '#52c41a' }}>{inr(v)}</span> },
  { title: 'Total Deposited', dataIndex: 'totalDeposited', key: 'totalDeposited', render: (v?: number) => <span style={{ color: '#1890ff' }}>{inr(v)}</span> },
  { title: 'Closing Balance', dataIndex: 'closingBalance', key: 'closingBalance', render: (v?: number) => <span style={{ fontWeight: 600 }}>{inr(v)}</span> },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const CashReports = () => {
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(defaultDateRange);
  const [summaryFilter, setSummaryFilter] = useState<SummaryFilter>('today');

  // Branch comes from the header's universal branch filter — no separate
  // filter on this page.
  const rawBranch = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof rawBranch === 'object' ? (rawBranch?._id || rawBranch?.id) : rawBranch;

  const { data: reportsResp, isFetching } = useGetCashReportsQuery({
    branchId: selectedBranchId || undefined,
    startDate: dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined,
    endDate:   dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined,
  });

  const { data: summaryResp, isFetching: summaryLoading } = useGetCashSummaryReportQuery({
    branchId: selectedBranchId || undefined,
    filter:   summaryFilter,
  });

  const apiData: any = (reportsResp as any)?.data ?? {};
  const branchRows: BranchReportRow[] = (apiData.branches || []).map((b: any) => ({
    key:            b.branchId,
    branchName:     b.branchName,
    // openingBalance: b.openingBalance,
    totalCollected: b.totalCollected,
    totalDeposited: b.totalDeposited,
    closingBalance: b.closingBalance,
  }));

  const summaryOverall: any = (summaryResp as any)?.data?.overall ?? {};

  return (
    <div className="cash-reports-page">
      <div className="header-section">
        <h2 style={{ margin: 0 }}>Cash Reports</h2>
        <div className="right-col">
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => setDateRange(v)}
          />
        </div>
      </div>

      <div className="cash-reports-section">
        {/* <div className="cash-reports-section-head">
          <h3>Summary Report</h3>
          <Select
            value={summaryFilter}
            onChange={setSummaryFilter}
            style={{ width: 180, height: 41 }}
            options={SUMMARY_FILTER_OPTIONS}
          />
        </div> */}
        <div className="cash-reports-stats-grid" style={{ opacity: summaryLoading ? 0.6 : 1 }}>
          <StatCard title="Collection"       value={inr(summaryOverall.collection)}      icon={<DollarOutlined />}            color="#52c41a" />
          <StatCard title="Vendor Payment"   value={inr(summaryOverall.vendorPayment)}   icon={<WalletOutlined />}            color="#f59e0b" />
          <StatCard title="Bank Deposit"     value={inr(summaryOverall.bankDeposit)}     icon={<BankOutlined />}              color="#1890ff" />
          <StatCard title="Last Outstanding" value={inr(summaryOverall.lastOutstanding)} icon={<ExclamationCircleOutlined />} color="#ff4d4f" />
        </div>
      </div>

      <div className="cash-reports-section cash-reports-table-wrapper">
        {/* <h3>Branch-wise Breakdown</h3> */}
        <CommonTable
          columns={columns}
          dataSource={branchRows}
          loading={isFetching}
          pagination={false}
          rowKey={(record: BranchReportRow) => record.key}
        />
      </div>
    </div>
  );
};

export default CashReports;
